/**
 * Issue #668 refinement — Mapper's audit trail: every source column the
 * Mapper considered before abstaining, with a 1-sentence rejection
 * reason for each. Surfaces the LLM's reasoning so the operator can
 * see "what did the system look at" without walking the full
 * correction history.
 *
 * Distinct from `CorrectionHistoryAccordion`:
 *   - Correction history = round-by-round repair attempts.
 *   - Considered sources = the LLM's enumeration at abstain time.
 *
 * Always rendered when `payload.considered_sources` is non-empty —
 * regardless of decision kind.
 */
import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { ConsideredSource } from '../../../services/mappingApi';

export const ConsideredSourcesAccordion: React.FC<{
  considered: ConsideredSource[];
}> = ({ considered }) => {
  if (!considered || considered.length === 0) return null;
  return (
    <Accordion
      disableGutters
      sx={{ mt: 1, boxShadow: 'none', border: '1px solid rgba(0,0,0,0.08)' }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 36 }}>
        <Typography variant="caption" color="text.secondary">
          Sources we looked at ({considered.length})
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 1 }}>
        <Stack spacing={0.75}>
          {considered.map((c, idx) => (
            <Box
              key={`${c.source_file}.${c.source_field}.${idx}`}
              sx={{
                p: 0.75,
                borderLeft: '2px solid rgba(0,0,0,0.16)',
                pl: 1,
              }}
            >
              <Typography
                variant="caption"
                sx={{ fontFamily: 'monospace', fontWeight: 600, display: 'block' }}
              >
                {c.source_file}.{c.source_field}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {c.rejection_reason}
              </Typography>
            </Box>
          ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

export default ConsideredSourcesAccordion;
